import { DocumentNode, gql, useMutation } from '@apollo/client';
import { Button, Text, useDisclosure, useToast } from '@chakra-ui/react';
import * as React from 'react';

import { EModItemResolutionState } from '@/client/legacy-shared-modules/moderation-item/moderationType';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { logError } from '@/imports/logging/ClientLogger';
import { AlertCircleIcon } from '@/imports/ui/chakra/feather';

import { PendingModerationState_CommentFragment } from './graphql-types/PendingModerationState_CommentFragment';
import {
  PendingModerationState_UpdateCommentModerationItem,
  PendingModerationState_UpdateCommentModerationItemVariables,
} from './graphql-types/PendingModerationState_UpdateCommentModerationItem';

const COMMENT_FRAGMENT = gql`
  fragment PendingModerationState_CommentFragment on Comment {
    _id
    moderationItem {
      _id
    }
    currentUserCanResolveModeratedState
  }
`;

const MODERATION_ITEM_MUTATION = gql`
  mutation PendingModerationState_UpdateCommentModerationItem(
    $input: UpdateModerationItemInputV2!
  ) {
    updateModerationItemV2(input: $input) {
      data {
        _id
        resolutionState
        comment {
          _id
          currentUserCanResolveModeratedState
        }
      }
    }
  }
`;

export const PendingModerationState: React.FC<{
  comment: PendingModerationState_CommentFragment;
}> & {
  commentFragment: DocumentNode;
} = ({ comment }) => {
  const confirmDialog = useDisclosure();

  const [editModerationItem] = useMutation<
    PendingModerationState_UpdateCommentModerationItem,
    PendingModerationState_UpdateCommentModerationItemVariables
  >(MODERATION_ITEM_MUTATION);

  const toast = useToast();

  const approve = async (): Promise<void> => {
    if (comment.moderationItem) {
      try {
        await editModerationItem({
          variables: {
            input: {
              id: comment.moderationItem._id,
              data: {
                resolutionState: EModItemResolutionState.APPROVED,
              },
            },
          },
        });
        toast({ title: 'This comment was approved', status: 'success' });
      } catch (e) {
        toast({ title: 'Failed to approve this comment', status: 'error' });
        logError(e);
      }
    } else {
      toast({ title: 'Failed to approve this comment', status: 'error' });
    }
  };

  const reject = async (): Promise<void> => {
    if (comment.moderationItem) {
      try {
        await editModerationItem({
          variables: {
            input: {
              id: comment.moderationItem._id,
              data: {
                resolutionState: EModItemResolutionState.REJECTED,
              },
            },
          },
        });
        toast({ title: 'This comment was rejected', status: 'success' });
      } catch (e) {
        toast({ title: 'Failed to reject this comment', status: 'error' });
        logError(e);
      }
    } else {
      toast({ title: 'Failed to reject this comment', status: 'error' });
    }
  };

  return (
    <>
      <AlertCircleIcon color="yellow.500" />
      <Text textStyle="compact" as="span" flexGrow="1">
        This comment is pending moderation
      </Text>
      {comment.currentUserCanResolveModeratedState && (
        <>
          <Button
            size="sm"
            variant="outline"
            colorScheme="gray"
            onClick={confirmDialog.onOpen}
          >
            Reject
          </Button>
          <Button
            size="sm"
            variant="outline"
            colorScheme="gray"
            onClick={approve}
          >
            Approve
          </Button>
          <ConfirmDialog
            isOpen={confirmDialog.isOpen}
            onClose={confirmDialog.onClose}
            title={'Are you sure you want to reject this comment?'}
            body={
              'Rejecting this comment will hide it from other community members. Are you sure you want to reject this comment? '
            }
            confirmButtonText={'Reject Comment'}
            onConfirm={reject}
          />
        </>
      )}
    </>
  );
};
PendingModerationState.commentFragment = COMMENT_FRAGMENT;
